import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { t } from "../i18n";
import { CaretDownIcon } from "./icons";
import { logout, type BillingPlan, type Me } from "../api";
import { PROVIDER_NAMES } from "../lib";
import { daysText, subscriptionEnded, useBillingState } from "../billing";
import { longDay } from "./SubscribePage";

/** The short plan note next to «Plan & billing», as in the Home mockup («trial · 3 days left»). */
function planNote(plan: BillingPlan): string {
  const date = plan.period_end ?? plan.ends_on;
  const day = date ? longDay(date) : "";
  switch (plan.state) {
    case "trial":
    case "trial-ending":
      return t("plan.menu.trial", { left: daysText(plan.days_left ?? 0) });
    case "expired":
      return t(subscriptionEnded(plan) ? "plan.menu.ended" : "plan.menu.expired");
    case "active":
      return plan.period ? t(plan.period === "yearly" ? "plan.menu.yearly" : "plan.menu.monthly", { date: day }) : "";
    case "past-due":
      return t("plan.menu.pastDue");
    case "paused":
      return t("plan.menu.paused");
    case "cancelled":
      return t("plan.menu.cancelled", { date: day });
    case "gift":
      return t("plan.menu.gift", { date: day });
    default:
      return t("plan.menu.comp");
  }
}

/** The signed-in user in the header, as in the mockups: the name opens Account, Plan & billing (BL-132), Connections, Data preview and Sign out; on a phone (no nav links) Install too. */
export function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { pathname, hash } = useLocation();
  const label = me.name || me.email || t("nav.account");
  const initial = label.trim().charAt(0).toUpperCase() || "?";
  // «Plan & billing» only where billing applies (not in the `off` mode of ADR-0048).
  const { state: billing } = useBillingState(true);
  const plan = billing && billing.plan.state !== "off" ? billing.plan : null;

  // A new page (or #plan on /account) closes the menu.
  useEffect(() => setOpen(false), [pathname, hash]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function signOut() {
    await logout();
    window.location.assign("/");
  }

  return (
    <div className="usermenu" ref={box}>
      <button
        type="button"
        className="btn usermenu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("menu.open")}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="usermenu-avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="usermenu-name">{label}</span>
        <span className="usermenu-caret" aria-hidden="true">
          <CaretDownIcon size={14} />
        </span>
      </button>
      {open && (
        <div className="usermenu-list" role="menu">
          <div className="usermenu-who">
            <span className="usermenu-avatar usermenu-avatar-lg" aria-hidden="true">
              {initial}
            </span>
            <span className="usermenu-id">
              <strong>{label}</strong>
              <span>{[me.email, PROVIDER_NAMES[me.provider]].filter(Boolean).join(" · ")}</span>
            </span>
          </div>
          <div className="usermenu-sep" role="separator" />
          <Link role="menuitem" to="/account">
            {t("nav.account")}
          </Link>
          {plan && (
            <Link role="menuitem" to="/account#plan">
              {t("plan.menu")}
              <span className="tag tag-accent usermenu-sub">{planNote(plan)}</span>
            </Link>
          )}
          <div className="usermenu-sep" role="separator" />
          <Link role="menuitem" to="/connections">
            {t("nav.connections")}
          </Link>
          <Link role="menuitem" to="/preview">
            {t("nav.preview")}
          </Link>
          <Link role="menuitem" to="/install" className="usermenu-phone">
            {t("nav.install")}
          </Link>
          <div className="usermenu-sep" role="separator" />
          <button role="menuitem" type="button" className="usermenu-out" onClick={signOut}>
            {t("account.logout")}
          </button>
        </div>
      )}
    </div>
  );
}
